import React, { useEffect, useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/BlogSlice";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import BlogListing from "./BlogListing";
import AddBlogWrapper from "../Add/AddBlogWrapper";
import EditBlogWrapper from "../Edit/EditBlogWrapper";
import {
  useChangeStatusBlogByIdMutation,
  useDeleteBlogByIdMutation,
  useGetAllBlogDataQuery,
} from "src/services/BlogService";
import { Tooltip } from "@mui/material";
import { usePermission } from "src/hooks/usePermission";

type Props = {};

const BlogListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    isOpenAddDialog,
    isOpenEditDialog,
    searchValue,
    isTableLoading,
  } = useSelector((state: RootState) => state.blog);
  const [selectedBlogId, setSelectedBlogId] = useState<string>();
  const [deleteBlog] = useDeleteBlogByIdMutation();
  const [changeStatusBlog] = useChangeStatusBlogByIdMutation();
  const { data, isLoading, isFetching } = useGetAllBlogDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["title"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  // Setting Module Data
  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const handleDeleteBlog = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Blog ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteBlog(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusBlog = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Blog ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusBlog({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const moduleName = "BLOG";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    {
      field: "title",
      headerName: "Title",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "description",
      headerName: "Description",
      flex: "flex-[1_1_0%]",
      renderCell: (params) => (
        <Tooltip
          title={
            <div
              style={{
                maxWidth: "400px",
                maxHeight: "300px",
                overflow: "auto",
                whiteSpace: "normal",
              }}
              dangerouslySetInnerHTML={{ __html: params.description }}
            />
          }
        >
          <div
            className="line-clamp-2 text-[14px] break-words max-w-[250px] prose"
            dangerouslySetInnerHTML={{ __html: params.description }}
          />
        </Tooltip>
      ),
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span>{row.tags?.join(", ")}</span>,
    },

    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "center",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedBlogId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusBlog(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteBlog(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      <div>
        <BlogListing
          columns={columns}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddBlogWrapper onClose={() => dispatch(setIsOpenAddDialog(false))} />
      ) : null}
      {isOpenEditDialog ? (
        <EditBlogWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedBlogId={selectedBlogId || ""}
        />
      ) : null}
    </>
  );
};

export default BlogListingWrapper;
